import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { userNotificationsActions } from '../actions/user-notifications.actions';
import { USER_NOTIFICATIONS_FEATURE_KEY } from '../constants/state-keys';
import { IUserNotificationsFeatureState } from '../interfaces/states/user-notifications-feature-state.interface';
import type { UserNotification } from '../actions/user-notifications.actions';

export function selectUserNotificationId(notification: UserNotification): string {
    return notification.id;
}

export const adapter: EntityAdapter<UserNotification> = createEntityAdapter<UserNotification>({
    selectId: selectUserNotificationId,
    sortComparer: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
});

export const selectFeature = createFeatureSelector<IUserNotificationsFeatureState>(USER_NOTIFICATIONS_FEATURE_KEY);

export const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors();

export const selectAllUserNotifications = createSelector(selectFeature, selectAll);
export const selectUserNotificationsEntities = createSelector(selectFeature, selectEntities);
export const selectUserNotificationsIds = createSelector(selectFeature, selectIds);
export const selectUserNotificationsTotal = createSelector(selectFeature, selectTotal);

export const selectUserNotificationsLoading = createSelector(selectFeature, (state) => state.loading);
export const selectUserNotificationsError = createSelector(selectFeature, (state) => state.error);
export const selectUserNotificationsFilter = createSelector(selectFeature, (state) => state.filter);
export const selectUserNotificationsOffset = createSelector(selectFeature, (state) => state.offset);
export const selectUserNotificationsLimit = createSelector(selectFeature, (state) => state.limit);
export const selectUserNotificationsUnreadCount = createSelector(selectFeature, (state) => state.unreadCount);

const initialState: IUserNotificationsFeatureState = adapter.getInitialState({
    loading: false,
    error: null,
    offset: 0,
    limit: 0,
    filter: {
        archived: false,
        read: undefined,
    },
    unreadCount: 0,
});

export const userNotificationsReducer = createReducer(
    initialState,
    on(userNotificationsActions.loadUserNotifications, (state, { request }) => ({
        ...state,
        loading: true,
        error: null,
        offset: request.offset,
        limit: request.limit,
        filter: request.filter,
    })),
    on(userNotificationsActions.setUserNotificationsFilter, (state, { filter }) => ({
        ...state,
        filter,
    })),
    on(userNotificationsActions.loadUserNotificationsSuccess, (state, { notifications, unreadCount, offset, limit }) => {
        return adapter.setAll(notifications, {
            ...state,
            loading: false,
            error: null,
            offset,
            limit,
            unreadCount,
        });
    }),
    on(userNotificationsActions.loadUserNotificationsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(userNotificationsActions.notificationReceived, (state, { notification }) => {
        // Only increment unreadCount when receiving a new notification via websocket
        const unreadCountIncrement = !notification.isRead && !notification.isArchived ? 1 : 0;
        return adapter.upsertOne(notification, {
            ...state,
            error: null,
            unreadCount: state.unreadCount + unreadCountIncrement,
        });
    }),
    on(userNotificationsActions.updateNotification, (state) => ({
        ...state,
        error: null,
    })),
    on(userNotificationsActions.updateNotificationSuccess, (state, { notifications, unreadCount }) => {
        const idsToRemove: string[] = [];
        const toUpsert: typeof notifications = [];
        const { archived, read } = state.filter;

        for (const n of notifications) {
            // Archived filter handling
            if (archived === false && n.isArchived) {
                idsToRemove.push(n.id);
                continue;
            }
            if (archived === true && !n.isArchived) {
                idsToRemove.push(n.id);
                continue;
            }

            // Read filter handling
            if (read === true && !n.isRead) {
                idsToRemove.push(n.id);
                continue;
            }
            if (read === false && n.isRead) {
                idsToRemove.push(n.id);
                continue;
            }

            toUpsert.push(n);
        }

        const baseState = idsToRemove.length ? adapter.removeMany(idsToRemove, state) : state;

        return adapter.upsertMany(toUpsert, {
            ...baseState,
            error: null,
            unreadCount,
        });
    }),
    on(userNotificationsActions.updateNotificationFailure, (state, { error }) => ({
        ...state,
        error,
    })),
);
